import React, { useEffect, useRef, useState, useCallback } from "react";
import Webcam from "react-webcam";
import axios from "@/lib/axios";
import { getDistance } from 'geolib';

import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API_BASE_URL } from '@/config';
import { useJourney } from "@/contexts/JourneyContext";
import { useLoader } from "@/contexts/LoaderContext";
import { Loader2, Camera, MapPin, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";

// ----------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------

interface BackendCoordinates {
  latitude: number;
  longitude: number;
}

interface AddressDetails {
  address_line1: string;
  address_line2: string;
  address_line3: string;
  landmark: string;
  pincode: string;
  address_type: string;
  address_label: string;
}

interface AddressOption extends AddressDetails {
  coord: BackendCoordinates;
}

interface ValidationThresholds {
  THRESHOLD_METERS: number;
  THRESHOLD_ACCURACY: number;
  THRESHOLD_STALE_DATA_MILLISEC: number;
}

interface GeoPosition {
  lat: number;
  lon: number;
  accuracy: number;
  timestamp: number;
}

type Capture = {
  dataUrl: string;
  lat: number;
  lon: number;
  distanceMeters: number;
  timestamp: number;
  accuracy: number;
};

interface SelfiePhotoLocVerificationV3Props {
  onDone?: () => void;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
}

// ----------------------------------------------------------------------
// CONSTANTS
// ----------------------------------------------------------------------

const UPLOAD_TIMEOUT_MS = 60000;

const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: "user", // Selfie mode
  width: { ideal: 1920 },
  height: { ideal: 1080 },
};

// ----------------------------------------------------------------------
// SUB-COMPONENTS
// ----------------------------------------------------------------------

const FullPageLoader = ({ message = "Loading..." }: { message?: string }) => (
  <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center text-white p-4">
    <Loader2 className="w-12 h-12 animate-spin mb-4 text-primary" />
    <p className="text-lg font-medium">{message}</p>
  </div>
);

const InstructionsScreen = ({
  addresses,
  selectedAddress,
  onAddressChange,
  thresholds,
  onProceed,
  isRequesting
}: {
  addresses: AddressOption[],
  selectedAddress: AddressDetails,
  onAddressChange: (addressType: string) => void,
  thresholds: ValidationThresholds,
  onProceed: () => void,
  isRequesting: boolean
}) => (
  <div className="min-h-screen bg-black text-white flex flex-col p-6 animate-in fade-in duration-300">
    <div className="flex-1 flex flex-col items-center justify-center text-center max-w-lg mx-auto space-y-6">
      <div className="text-2xl font-semibold mb-1">INSTRUCTIONS</div>

      <div className="mb-2 rounded-lg bg-gray-100 p-3 text-center text-xs text-gray-600 font-bold w-full">
        By proceeding, you consent to the use of your camera, audio, video, and location for identity and location verification only.
      </div>

      {/* Address Selection Dropdown */}
      {addresses.length > 1 && (
        <div className="w-full">
          <label className="text-sm font-semibold text-gray-300 mb-2 block text-left">
            Select Address for Selfie Verification:
          </label>
          <Select value={selectedAddress.address_type} onValueChange={onAddressChange}>
            <SelectTrigger className="w-full bg-white/10 border-white/20 text-white h-12 text-base">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {addresses.map((addr) => (
                <SelectItem key={addr.address_type} value={addr.address_type}>
                  {addr.address_label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="text-sm">
        This selfie must be taken at the following address as provided by you in the application:
      </div>

      <div className="bg-white/10 rounded-xl p-4 w-full border border-white/10 backdrop-blur-sm text-left">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">SELECTED ADDRESS:</h3>
        <div className="space-y-1 text-sm font-medium">
          <p className="text-intellend-green font-bold mb-2">{selectedAddress.address_label}</p>
          <p>{selectedAddress.address_line1}</p>
          <p>{selectedAddress.address_line2}</p>
          {selectedAddress.address_line3 && <p>{selectedAddress.address_line3}</p>}
          <p>{selectedAddress.landmark}</p>
          <p className="text-primary pt-1">{selectedAddress.pincode}</p>
        </div>
      </div>

      <div className="space-y-3 text-sm text-gray-300 text-left w-full">
        <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
          <span>Keep your face fully visible and centered in the frame.</span>
        </div>
        <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
          <span>Prefer a plain background without distractions.</span>
        </div>
        <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
          <span>Ensure good lighting; face the light source to avoid shadows.</span>
        </div>
        <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg">
          <MapPin className="w-5 h-5 text-blue-500 shrink-0" />
          <span>Take the selfie within {thresholds.THRESHOLD_METERS} meters of the above address to boost chances of loan approval.</span>
        </div>
      </div>

    </div>

    <div className="pt-6 w-full max-w-lg mx-auto">
      <Button size="lg" className="w-full text-lg font-semibold h-12" onClick={onProceed} disabled={isRequesting}>
        {isRequesting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Requesting Access...
          </>
        ) : (
          "Proceed"
        )}
      </Button>
      {isRequesting && (
        <p className="text-xs text-gray-400 text-center mt-3">
          This can take up to 2 minutes
        </p>
      )}
    </div>
  </div>
);

const PermissionRequestScreen = ({
  type,
  onGrant,
  onInstruction
}: {
  type: "camera" | "location",
  onGrant: () => void,
  onInstruction: () => void
}) => {
  const isCam = type === "camera";
  const Icon = isCam ? Camera : MapPin;
  const title = isCam ? "Camera Permission Required" : "Location Required";

  const desc = isCam
    ? "We need camera access to capture your selfie."
    : "Location access is mandatory to verify this selfie.\nPlease enable location permissions in your browser or device settings.";

  return (
    <div className="fixed inset-0 bg-black/95 z-40 flex flex-col items-center justify-center p-6 text-center text-white">
      <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mb-6 animate-pulse">
        <Icon className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-xl font-semibold mb-3">{title}</h2>

      {!isCam && (
        <div className="text-xs text-gray-400 mb-6 font-mono">
          Settings → Site Settings → Location → Allow
        </div>
      )}

      <p className="text-gray-300 mb-8 max-w-xs mx-auto text-sm leading-relaxed whitespace-pre-line">{desc}</p>

      <div className="flex flex-col gap-3 w-full max-w-xs">
        <Button size="lg" onClick={onGrant} className="w-full bg-white text-black hover:bg-gray-200">
          {isCam ? "Allow Camera" : "Allow Location"}
        </Button>
        <Button variant="outline" onClick={onInstruction} className="w-full bg-white text-black hover:bg-gray-200">
          Instructions For Apple Devices
        </Button>

        {!isCam && (
          <Button
            className="w-full mt-2"
            onClick={() => window.location.reload()}
          >
            Retry After Enabling Location
          </Button>
        )}
      </div>
    </div>
  );
};


// ----------------------------------------------------------------------
// MAIN PAGE
// ----------------------------------------------------------------------

export default function SelfiePhotoLocVerificationV3({ onDone, registerSubmit }: SelfiePhotoLocVerificationV3Props) {
  const navigate = useNavigate();
  const webcamRef = useRef<Webcam | null>(null);

  // -- Global Loader (for submission) --
  const { setLoading: setGlobalLoading } = useLoader();
  const { refresh: refreshJourney } = useJourney();

  // -- State --
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [availableAddresses, setAvailableAddresses] = useState<AddressOption[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<AddressOption | null>(null);
  const [thresholds, setThresholds] = useState<ValidationThresholds | null>(null);

  const [step, setStep] = useState<"instructions" | "permissions" | "capture" | "preview">("instructions");

  // Permissions State
  const [permissionStatus, setPermissionStatus] = useState<{
    camera: "unknown" | "granted" | "denied";
    location: "unknown" | "granted" | "denied";
  }>({ camera: "unknown", location: "unknown" });

  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);

  // Capture State
  const [position, setPosition] = useState<GeoPosition | null>(null);
  const [capture, setCapture] = useState<Capture | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [webcamKey, setWebcamKey] = useState(0);

  // -- 1. Load Data on Mount --
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingInitial(true);
        const res = await axios.post(`${API_BASE_URL}/api/get_addresses_for_selfie_selection/`);

        const addresses: AddressOption[] = res.data.addresses.map((addr: any) => ({
          address_line1: addr.address_line1,
          address_line2: addr.address_line2,
          address_line3: addr.address_line3,
          landmark: addr.landmark,
          pincode: addr.pincode,
          address_type: addr.address_type,
          address_label: addr.address_label,
          coord: {
            latitude: Number(addr.latitude),
            longitude: Number(addr.longitude),
          },
        }));

        setAvailableAddresses(addresses);
        setThresholds({
          THRESHOLD_METERS: res.data.THRESHOLD_METERS ?? 100,
          THRESHOLD_ACCURACY: res.data.THRESHOLD_ACCURACY ?? 100,
          THRESHOLD_STALE_DATA_MILLISEC: res.data.THRESHOLD_STALE_DATA_MILLISEC ?? 30000,
        });

        // Auto-select the first address
        if (addresses.length > 0) {
          setSelectedAddress(addresses[0]);
        }
      } catch (err: any) {
        let message = "Unable to fetch address details";
        if (axios.isAxiosError(err)) {
          if (err.response?.data?.error) {
            message = err.response.data.error;
          } else if (err.request) {
            message = "Network error. Please try again.";
          }
        }
        console.error("Failed to load backend data:", err);
        alert(message);
      } finally {
        setLoadingInitial(false);
      }
    };
    fetchData();
  }, []);

  // -- 2. Distance Calculation --
  const computeDistance = (lat: number, lon: number) => {
    if (!selectedAddress?.coord) return -1;
    return Math.round(getDistance({ latitude: lat, longitude: lon }, selectedAddress.coord));
  };

  // Distance zone: <100 = ok (no warning), 100-500 = amber (green info), >500 = red warning
  const AMBER_THRESHOLD = 500;
  const distZone = (dist: number): 'ok' | 'amber' | 'red' | 'unknown' => {
    if (dist === -1) return 'unknown';
    if (dist < 100) return 'ok';
    if (dist <= AMBER_THRESHOLD) return 'amber';
    return 'red';
  };

  // -- 3. Location Watcher --
  const watchIdRef = useRef<number | null>(null);

  const startWatching = useCallback(() => {
    if (!navigator.geolocation || !selectedAddress || !thresholds) return;

    if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setPermissionStatus(prev => ({ ...prev, location: "granted" }));

        const { THRESHOLD_STALE_DATA_MILLISEC, THRESHOLD_ACCURACY } = thresholds;

        if (Date.now() - pos.timestamp > THRESHOLD_STALE_DATA_MILLISEC) {
          setGeoError("Stale GPS data.");
          return;
        }
        if (pos.coords.accuracy > THRESHOLD_ACCURACY) {
          setGeoError("Low GPS accuracy: " + pos.coords.accuracy);
          return;
        }

        setGeoError(null);
        setPosition({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: pos.timestamp,
        });
      },
      (err) => {
        console.warn("Geo Watch Error", err);
        setGeoError("Location access unavailable.");
        if (err.code === 1) setPermissionStatus(prev => ({ ...prev, location: "denied" }));
      },
      { enableHighAccuracy: true, maximumAge: 60000, timeout: 30000 }
    );
  }, [selectedAddress, thresholds]);

  // -- 4. Permission Handlers --
  const requestPermissions = async () => {
    setIsRequestingPermissions(true);

    let locGranted = false;
    let camGranted = false;

    // 1. Location
    try {
      await new Promise<void>((resolve, reject) => {
        if (!navigator.geolocation) return reject();
        navigator.geolocation.getCurrentPosition(
          () => {
            setPermissionStatus(p => ({ ...p, location: "granted" }));
            startWatching();
            locGranted = true;
            resolve();
          },
          (err) => {
            console.warn("Location error:", err);
            setPermissionStatus(p => ({ ...p, location: "denied" }));
            reject();
          }
        );
      });
    } catch { }

    // 2. Camera
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(t => t.stop());
      setPermissionStatus(p => ({ ...p, camera: "granted" }));
      camGranted = true;
    } catch (err) {
      console.warn("Camera error", err);
      setPermissionStatus(p => ({ ...p, camera: "denied" }));
    }

    setIsRequestingPermissions(false);

    if (locGranted && camGranted) {
      setStep("capture");
    } else {
      setStep("permissions");
    }
  };

  useEffect(() => {
    if (step === "permissions") {
      if (permissionStatus.camera === "granted" && permissionStatus.location === "granted") {
        setStep("capture");
      }
    }
  }, [permissionStatus, step]);

  // Register submit handler for Journey Continue button
  useEffect(() => {
    if (registerSubmit) {
      registerSubmit(async () => {
        if (!capture) {
          alert("Please capture a selfie first");
          return false;
        }
        const result = await handleSubmit();
        return result;
      });
    }
  }, [registerSubmit, capture]);

  const showIOSHelp = (type: "location" | "camera") => {
    if (type === 'location') {
      const userConfirmed = confirm(
        "To enable location access in iOS:\n\n" +
        "1. Go to Settings -> Privacy & Security -> Location Services\n" +
        "2. Activate Location Services\n" +
        "3. Safari Websites -> While Using the App\n\n" +
        "After doing this you may need to close the tab to reflect changes Press OK and close the tab, or Cancel to reload the page."
      );
      if (!userConfirmed) window.location.reload();
    } else {
      alert(
        "To enable camera access on iPhone:\n\n" +
        "1. Go to Settings → Privacy & Security → Camera\n" +
        "2. Enable Camera access for Safari / your browser\n" +
        "3. Reload this page after enabling"
      );
    }
  };

  // -- 5. Capture & Submit --
  const handleCapture = useCallback(() => {
    if (!webcamRef.current || !position || !selectedAddress) return;
    const screenshot = webcamRef.current.getScreenshot();
    if (!screenshot) return;

    setCapture({
      dataUrl: screenshot,
      lat: position.lat,
      lon: position.lon,
      distanceMeters: computeDistance(position.lat, position.lon),
      accuracy: position.accuracy,
      timestamp: Date.now()
    });
    setStep("preview");
  }, [position, selectedAddress]);

  const dataURLtoBlob = (dataurl: string): Blob => {
    const [meta, base64] = dataurl.split(",");
    const mime = meta.match(/:(.*?);/)?.[1] || "image/jpeg";
    const binary = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    return new Blob([binary], { type: mime });
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (!capture) return false;
    setIsSubmitting(true);
    setGlobalLoading(true);

    const form = new FormData();
    form.append("file", dataURLtoBlob(capture.dataUrl), `selfie-${capture.timestamp}.jpg`);
    form.append("latitude", String(capture.lat));
    form.append("longitude", String(capture.lon));
    form.append("distance_meters", String(capture.distanceMeters));
    form.append("accuracy", String(capture.accuracy));

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);

      const res = await axios.post(
        `${API_BASE_URL}/api/set_selfie_photo_geolocation_pushload/`,
        form,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (res.status >= 200 && res.status < 300) {
        // ✅ Backend advances journey stage via /state and /event APIs
        // ✅ Refresh journey context to sync with backend state
        console.log("✅ Selfie uploaded successfully, refreshing journey state...");
        
        await refreshJourney();
        console.log("✅ Journey state refreshed");
        
        // ✅ Call onDone callback if provided (from wrapper component)
        // This will trigger JourneyPage to fetch next page - NO PAGE RELOAD
        if (onDone) {
          console.log("✅ Calling onDone callback to advance journey");
          onDone();
        }
        return true;
      }
      return false;
    } catch (err: any) {
      console.warn('Upload failed or timed out:', err?.message);
      alert("Upload failed. Please try again. Do not close the tab / browser");
      // Refresh journey state even if upload failed (will retry in background)
      refreshJourney().catch((journeyErr) => {
        console.error("Failed to refresh journey state:", journeyErr);
      });
      return false;
    } finally {
      setIsSubmitting(false);
      setGlobalLoading(false);
    }
  };

  // Address change handler
  const handleAddressChange = (addressType: string) => {
    const newAddress = availableAddresses.find(addr => addr.address_type === addressType);
    if (newAddress) {
      setSelectedAddress(newAddress);
    }
  };

  // -- RENDER --

  if (loadingInitial || !thresholds) return <FullPageLoader message="Fetching address details..." />;

  if (!selectedAddress) return <FullPageLoader message="Loading address..." />;

  // 1. Instructions
  if (step === "instructions") {
    return (
      <InstructionsScreen
        addresses={availableAddresses}
        selectedAddress={selectedAddress}
        onAddressChange={handleAddressChange}
        thresholds={thresholds}
        onProceed={requestPermissions}
        isRequesting={isRequestingPermissions}
      />
    );
  }

  // 2. Permissions Logic
  if (step === "permissions") {
    if (permissionStatus.location !== "granted") {
      return (
        <PermissionRequestScreen
          type="location"
          onGrant={() => {
            navigator.geolocation.getCurrentPosition(
              () => {
                setPermissionStatus(p => ({ ...p, location: "granted" }));
                startWatching();
              },
              (e) => {
                console.warn(e);
                setPermissionStatus(p => ({ ...p, location: "denied" }));
              },
              { enableHighAccuracy: true }
            );
          }}
          onInstruction={() => showIOSHelp("location")}
        />
      );
    }
    if (permissionStatus.camera !== "granted") {
      return (
        <PermissionRequestScreen
          type="camera"
          onGrant={async () => {
            try {
              const s = await navigator.mediaDevices.getUserMedia({ video: true });
              s.getTracks().forEach(t => t.stop());
              setPermissionStatus(p => ({ ...p, camera: "granted" }));
            } catch {
              setPermissionStatus(p => ({ ...p, camera: "denied" }));
            }
          }}
          onInstruction={() => showIOSHelp("camera")}
        />
      );
    }
  }

  // 3. Capture & Preview
  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {/* HEADER */}
      <div className="absolute top-0 inset-x-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4 pt-6">
        <h2 className="text-white text-center font-bold text-lg drop-shadow-md leading-tight">
          Please adjust your face in center and take a photo.
        </h2>
      </div>

      <div className="relative flex-1 bg-gray-900 overflow-hidden">
        {step === "capture" ? (
          <>
            <Webcam
              key={webcamKey}
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={VIDEO_CONSTRAINTS}
              className="absolute inset-0 w-full h-full object-cover transform scale-x-[-1]"
              onUserMediaError={() => setPermissionStatus(p => ({ ...p, camera: "denied" }))}
            />

            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                className="rounded-full border-2 border-white opacity-75"
                style={{
                  width: '70%',
                  height: '40%',
                  backgroundColor: 'transparent',
                }}
              />
            </div>

            {/* Live Stats */}
            <div className="absolute bottom-6 inset-x-6 flex flex-col gap-4 z-30">
              {position && (() => {
                const dist = computeDistance(position.lat, position.lon);
                const zone = distZone(dist);
                return (
                  <div className="text-center">
                    <p className="text-white text-sm font-medium drop-shadow-md">
                      Distance: {dist} m | Accuracy: {position.accuracy.toFixed(2)}
                    </p>
                    {zone === 'amber' && (
                      <p className="text-green-400 text-xs font-semibold max-w-xs mx-auto text-center break-words bg-black/40 p-1 rounded mt-1">
                        You are {dist} m away — within acceptable range.
                      </p>
                    )}
                    {zone === 'red' && (
                      <p className="text-red-400 text-xs font-semibold max-w-xs mx-auto text-center break-words bg-black/40 p-1 rounded mt-1">
                        Move closer to the selected address for better verification.
                      </p>
                    )}
                  </div>
                );
              })()}

              {geoError && <p className="text-red-500 text-sm font-bold text-center bg-black/50 p-2 rounded">{geoError}</p>}

              <div className="flex justify-center">
                <button
                  onClick={handleCapture}
                  disabled={!!geoError || !position}
                  className="w-20 h-20 rounded-full bg-white border-4 border-gray-300 shadow-lg active:scale-95 transition-all flex items-center justify-center disabled:opacity-50 disabled:scale-100"
                >
                  <div className="w-16 h-16 rounded-full border-2 border-black" />
                </button>
              </div>
            </div>
          </>
        ) : (
          capture && (
            <div className="relative w-full h-full flex flex-col">
              <img src={capture.dataUrl} alt="Preview" className="flex-1 object-cover" />

              {/* Validation Status Overlay */}
              <div className="absolute bottom-0 inset-x-0 bg-black/80 backdrop-blur-xl p-6 rounded-t-2xl z-40 border-t border-white/10">
                <div className="flex flex-col items-center text-center gap-2 mb-6">
                  {(() => {
                    const zone = distZone(capture.distanceMeters);
                    const textCls =
                      zone === 'unknown' ? "text-yellow-400"
                      : zone === 'red'   ? "text-red-400"
                      :                    "text-green-400"; // ok or amber both green
                    return (
                      <>
                        <p className={`text-sm font-semibold ${textCls}`}>
                          Distance: {capture.distanceMeters} m from selected address.
                        </p>

                        {zone === 'amber' && (
                          <div className="text-green-400 text-xs mt-2 max-w-[90%] break-words text-center space-y-1">
                            <p>You are within acceptable range. You may submit this photo.</p>
                          </div>
                        )}

                        {zone === 'red' && (
                          <div className="text-red-400 text-xs mt-2 max-w-[90%] break-words text-center space-y-1">
                            <p>You may submit this photo.</p>
                            <p>OR</p>
                            <p>Take another photo within {thresholds.THRESHOLD_METERS} meters of the address.</p>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div className="flex gap-4">
                  <Button variant="secondary" className="flex-1 h-12" onClick={() => {
                    setCapture(null);
                    setStep("capture");
                  }}>
                    <RefreshCw className="mr-2 w-4 h-4" /> Capture Again
                  </Button>
                  <Button
                    className="flex-1 h-12"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
